import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Play } from 'lucide-react';
import { practiceInfo } from '../data/siteData';
import './DentalVideosPage.css';

function DentalVideosPage() {
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [videoThumbnails, setVideoThumbnails] = useState({});

  const toggleAccordion = (index) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  // Generate thumbnail from video file
  const generateThumbnail = (videoUrl, videoId) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        // Seek to 2 seconds or 10% of video duration for better thumbnail
        video.currentTime = Math.min(30, video.duration * 0.2);
      };
      
      video.onseeked = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 640;
        canvas.height = 360;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.85);
        resolve(thumbnailUrl);
      };
      
      video.onerror = () => {
        // Fallback to placeholder if video fails to load
        resolve('https://via.placeholder.com/640x360/20B2AA/ffffff?text=Video+Thumbnail');
      };
      
      video.src = videoUrl;
    });
  };

  // Load thumbnails when component mounts
  useEffect(() => {
    const loadThumbnails = async () => {
      const thumbnails = {};
      
      for (const category of videoCategories) {
        for (const video of category.videos) {
          const thumbnail = await generateThumbnail(video.videoUrl, video.id);
          thumbnails[video.id] = thumbnail;
        }
      }
      
      setVideoThumbnails(thumbnails);
    };
    
    loadThumbnails();
  }, []);

  // Video categories with 3 videos each (using local video paths)
  const videoCategories = [
    {
      id: 1,
      title: 'Exams/Hygiene',
      description: 'Routine dental check-ups and professional cleanings to maintain healthy teeth and gums and prevent oral problems.',
      videos: [
        {
          id: 'v1',
          title: 'New Patient Exam',
          videoUrl: '/videos/exams-hygiene/New_Patient_Exam.mp4',
          description: 'A first dental visit to assess oral health and plan treatment.'
        },
        {
          id: 'v2',
          title: 'Pediatric First Visit',
          videoUrl: '/videos/exams-hygiene/Pediatric_First_Visit.mp4',
          description: 'A childs first dental visit to check oral health and build comfort with dental care.'
        },
        {
          id: 'v3',
          title: 'Brushing & Flossing',
          videoUrl: '/videos/exams-hygiene/Brushing_Flossing.mp4',
          description: 'Guidance on proper brushing and flossing techniques to maintain healthy teeth and gums.'
        }
      ]
    },
    {
      id: 2,
      title: 'Cosmetic',
      description: 'Dental treatments focused on improving the appearance of your smile.',
      videos: [
        {
          id: 'v4',
          title: 'Composite Filling',
          videoUrl: '/videos/cosmetic/Composite_Filling.mp4',
          description: 'A tooth-colored filling used to repair cavities and restore the natural look of teeth.'
        },
        {
          id: 'v31',
          title: 'Home Teeth Whitening',
          videoUrl: '/videos/cosmetic/Home_Teeth_Whitening.mp4',
          description: 'Take-home whitening treatment to safely brighten your smile.'
        },
        {
          id: 'v32',
          title: 'Implant for Crown & Bridge',
          videoUrl: '/videos/cosmetic/Implant_for_Crown_Bridge.mp4',
          description: 'Dental implants used to support crowns or bridges for missing teeth.'
        },
        {
          id: 'v33',
          title: 'In Office Teeth Whitening',
          videoUrl: '/videos/cosmetic/In_Office_Teeth_Whitening.mp4',
          description: 'Professional whitening treatment performed at the clinic for faster, brighter results.'
        },
        {
          id: 'v34',
          title: 'Inlay Filling',
          videoUrl: '/videos/cosmetic/Inlay_Filling.mp4',
          description: 'A custom-made filling used to restore damaged teeth and strengthen their structure.'
        },
        {
          id: 'v35',
          title: 'Porcelain Crown',
          videoUrl: '/videos/cosmetic/Porcelain_Crown.mp4',
          description: 'A natural-looking ceramic cap that restores and protects a damaged tooth.'
        },
        {
          id: 'v36',
          title: 'Porcelain Inlay',
          videoUrl: '/videos/cosmetic/Porcelain_Inlay.mp4',
          description: 'A custom ceramic restoration used to repair and strengthen a damaged tooth.'
        },
        {
          id: 'v37',
          title: 'Veneer',
          videoUrl: '/videos/cosmetic/Veneer.mp4',
          description: 'A thin porcelain shell placed on the front of a tooth to improve its appearance.'
        }
      ]
    },
    {
      id: 3,
      title: 'Periodontics',
      description: 'Specialized care focused on treating gum disease and maintaining healthy gums and supporting tissues.',
      videos: [
        {
          id: 'v7',
          title: 'Deep Cleaning',
          videoUrl: '/videos/periodontics/Deep_Cleaning.mp4',
          description: 'A thorough cleaning below the gum line to treat gum disease and remove plaque and tartar.'
        },
        {
          id: 'v8',
          title: 'Implant for Crown & Bridge',
          videoUrl: '/videos/periodontics/Implant_for_Crown_Bridge.mp4',
          description: 'Dental implants that replace missing teeth and support crowns or bridges.'
        },
        {
          id: 'v9',
          title: 'Implant for Denture',
          videoUrl: '/videos/periodontics/Implant_for_Denture.mp4',
          description: 'Dental implants used to securely support and stabilize dentures.'
        },
        {
          id: 'v39',
          title: 'In Office Cleaning',
          videoUrl: '/videos/periodontics/In_Office_Cleaning.mp4',
          description: 'Professional dental cleaning to remove plaque and tartar and keep teeth healthy.'
        }
      ]
    },
    {
      id: 4,
      title: 'Dental Implants',
      description: 'Artificial tooth roots placed in the jaw to replace missing teeth.',
      videos: [
        {
          id: 'v10',
          title: 'Implant for Crown Bridge',
          videoUrl: '/videos/dental-implants/Implant_for_Crown_Bridge.mp4',
          description: 'Dental implants used to support crowns or bridges for missing teeth.'
        },
        {
          id: 'v11',
          title: 'Implant for Denture',
          videoUrl: '/videos/dental-implants/Implant_for_Denture.mp4',
          description: 'Dental implants that securely hold and support dentures.'
        },
      ]
    },
    {
      id: 5,
      title: 'Pediatric',
      description: 'Dental care specialized for children’s oral health and development.',
      videos: [
        {
          id: 'v13',
          title: 'Pediatric First Visit',
          videoUrl: '/videos/pediatric/Pediatric_First_Visit.mp4',
          description: 'A child’s first dental appointment to check oral health and ensure a comfortable experience.'
        },
        {
          id: 'v14',
          title: 'Pediatric Teeth Cleaning',
          videoUrl: '/videos/pediatric/Pediatric_Teeth_Cleaning.mp4',
          description: 'Professional cleaning for children to remove plaque and keep teeth healthy.'
        },
        {
          id: 'v15',
          title: 'Sealants & Sports Guard',
          videoUrl: '/videos/pediatric/Sealants_Sports_Guard.mp4',
          description: 'Protective treatments to prevent cavities and shield teeth during sports.'
        }
      ]
    },
    {
      id: 6,
      title: 'Endodontics',
      description: 'Specialized treatment focused on the inside of the tooth, including root canal therapy.',
      videos: [
        {
          id: 'v16',
          title: 'Alternatives to Root Canal',
          videoUrl: '/videos/endodontics/Alternatives_to_Root_Canal.mp4',
          description: 'Treatment options to manage tooth infection or damage without performing a root canal.'
        },
        {
          id: 'v17',
          title: 'Root Canal',
          videoUrl: '/videos/endodontics/Root_Canal.mp4',
          description: 'Treatment to remove infected tissue from inside a tooth and save it.'
        },
      ]
    },
    {
      id: 7,
      title: 'Oral Surgery',
      description: 'Surgical procedures to treat dental issues such as extractions and jaw problems.',
      videos: [
        {
          id: 'v19',
          title: 'Wisdom Teeth',
          videoUrl: '/videos/oral-surgery/Wisdom_Teeth.mp4',
          description: 'Removal or treatment of third molars to prevent pain and dental problems.'
        },
        {
          id: 'v20',
          title: 'Implant for Crown & Bridge',
          videoUrl: '/videos/oral-surgery/Implant_for_Crown_Bridge.mp4',
          description: 'Dental implants used to replace missing teeth and support crowns or bridges.'
        },
        {
          id: 'v21',
          title: 'Implant for Denture',
          videoUrl: '/videos/oral-surgery/Implant_for_Denture.mp4',
          description: 'Dental implants used to anchor and stabilize dentures.'
        },
		{
          id: 'v40',
          title: 'Post Surgery Instructions',
          videoUrl: '/videos/oral-surgery/Post_Surgery_Instructions.mp4',
          description: 'Guidelines to help ensure proper healing and recovery after dental surgery.'
        },
		{
          id: 'v41',
          title: 'Tooth Extractoin',
          videoUrl: '/videos/oral-surgery/Tooth_Extractoin.mp4',
          description: 'Removal of a tooth that is damaged, infected, or causing problems.'
        }
      ]
    },
    {
      id: 8,
      title: 'Crown & Bridge',
      description: 'Straightening teeth for a healthy, beautiful smile',
      videos: [
        {
          id: 'v22',
          title: 'Gold Crown',
          videoUrl: '/videos/crown-bridge/Gold_Crown.mp4',
          description: 'A durable metal cap used to restore and protect a damaged tooth.'
        },
        {
          id: 'v23',
          title: 'Implant for Crown & Bridge',
          videoUrl: '/videos/crown-bridge/Implant_for_Crown_Bridge.mp4',
          description: 'Dental implants that replace missing teeth and support crowns or bridges.'
        },
        {
          id: 'v24',
          title: 'Porcelain Metal Crown',
          videoUrl: '/videos/crown-bridge/Porcelain_Metal_Crown.mp4',
          description: 'A crown with a metal base and porcelain outer layer for strength and natural appearance.'
        },
		{
          id: 'v42',
          title: 'Porcelan Crown',
          videoUrl: '/videos/crown-bridge/Porcelan_Crown.mp4',
          description: 'A ceramic cap used to restore and protect a damaged tooth with a natural look.'
        },
		{
          id: 'v43',
          title: 'Veneer',
          videoUrl: '/videos/crown-bridge/Veneer.mp4',
          description: 'A thin shell placed on the front of a tooth to improve its appearance.'
        },
		{
          id: 'v44',
          title: 'Crown',
          videoUrl: '/videos/crown-bridge/Crown.mp4',
          description: 'A cap placed over a tooth to restore its shape, strength, and appearance.'
        },
		{
          id: 'v45',
          title: 'Crown Material Comparision',
          videoUrl: '/videos/crown-bridge/Crown_Material_Comparision.mp4',
          description: 'An overview of different crown types to help choose the best option for your tooth.'
        },
		{
          id: 'v46',
          title: 'Dental Bridge',
          videoUrl: '/videos/crown-bridge/Dental_Bridge.mp4',
          description: 'A fixed restoration that replaces one or more missing teeth by anchoring to adjacent teeth.'
        }
      ]
    },
    {
      id: 9,
      title: 'Dentures',
      description: 'Removable appliances used to replace missing teeth and restore chewing and appearance.',
      videos: [
        {
          id: 'v25',
          title: 'Denture Home Care',
          videoUrl: '/videos/dentures/Denture_Home_Care.mp4',
          description: 'Instructions for cleaning and maintaining dentures to keep them comfortable and long-lasting.'
        },
        {
          id: 'v26',
          title: 'Immediate Denture',
          videoUrl: '/videos/dentures/Immediate_Denture.mp4',
          description: 'A denture placed right after tooth extraction to replace teeth immediately.'
        },
        {
          id: 'v27',
          title: 'Implant for Denture',
          videoUrl: '/videos/dentures/Implant_for_Denture.mp4',
          description: 'Dental implants used to securely support and stabilize dentures.'
        },
		{
          id: 'v47',
          title: 'Partial Denture',
          videoUrl: '/videos/dentures/Partial_Denture.mp4',
          description: 'A removable appliance that replaces one or more missing teeth while preserving remaining natural teeth.'
        },
		{
          id: 'v48',
          title: 'Temporary Partial Denture',
          videoUrl: '/videos/dentures/Temporary_Partial_Denture.mp4',
          description: 'A short-term removable denture used to replace missing teeth while awaiting permanent treatment.'
        },
		{
          id: 'v49',
          title: 'Denture',
          videoUrl: '/videos/dentures/Denture.mp4',
          description: 'A removable appliance used to replace missing teeth and restore function and appearance.'
        }
      ]
    },
    {
      id: 10,
      title: 'Other',
      description: 'Additional dental services not listed under specific categories.',
      videos: [
        {
          id: 'v28',
          title: 'Amalgam Filling',
          videoUrl: '/videos/other/Amalgam_Filling.mp4',
          description: 'A durable silver-colored filling used to repair cavities in teeth.'
        },
        {
          id: 'v29',
          title: 'Dental Filling Materials',
          videoUrl: '/videos/other/Dental_Filling_Materials.mp4',
          description: 'Different types of materials used to fill and restore cavities in teeth.'
        },
        {
          id: 'v30',
          title: 'Night Guard',
          videoUrl: '/videos/other/Night_Guard.mp4',
          description: 'A custom oral appliance worn during sleep to protect teeth from grinding or clenching.'
        }
      ]
    }
  ];

  const [activeVideo, setActiveVideo] = useState(null);

  const playVideo = (video) => {
    setActiveVideo(video);
  };

  const closeVideoPlayer = () => {
    setActiveVideo(null);
  };

  return (
    <main className="dental-videos-page">
      {/* Page Hero */}
      <div className="page-hero">
        <div className="page-hero-overlay"></div>
        <div className="container">
          <nav className="breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <ChevronRight size={14} />
            <Link to="/education-and-resources">Education and Resources</Link>
            <ChevronRight size={14} />
            <span>Dental Videos</span>
          </nav>
          <h1>Dental Videos</h1>
           <p className="page-subtitle">
            Learn more about dental procedures, treatments, and oral health care
          </p>
        </div>
      </div>

      {/* Video Content */}
      <div className="videos-content">
        <div className="container">
          <div className="intro-text">
            <p>
              Our educational video library is designed to help you understand various dental procedures and treatments. 
              Each video provides clear, easy-to-understand information about what to expect during your dental care.
            </p>
          </div>

          {/* Video Accordions */}
          <div className="video-accordions">
            {videoCategories.map((category, index) => (
              <div key={category.id} className="accordion-item">
                <button
                  className={`accordion-header ${activeAccordion === index ? 'active' : ''}`}
                  onClick={() => toggleAccordion(index)}
                  aria-expanded={activeAccordion === index}
                >
                  <div className="accordion-title">
                    <h2>{category.title}</h2>
                    <p className="accordion-description">{category.description}</p>
                  </div>
                  <ChevronDown 
                    className={`accordion-icon ${activeAccordion === index ? 'rotated' : ''}`} 
                    size={24} 
                  />
                </button>

                <div className={`accordion-content ${activeAccordion === index ? 'active' : ''}`}>
                  <div className="video-grid">
                    {category.videos.map((video) => (
                      <div key={video.id} className="video-card" onClick={() => playVideo(video)}>
                        <div className="video-thumbnail">
                          {videoThumbnails[video.id] ? (
                            <img src={videoThumbnails[video.id]} alt={video.title} />
                          ) : (
                            <div className="thumbnail-loading">
                              <div className="loading-spinner"></div>
                              <span>Loading thumbnail...</span>
                            </div>
                          )}
                          <div className="play-overlay">
                            <Play size={48} />
                          </div>
                        </div>
                        <div className="video-info">
                          <h3>{video.title}</h3>
                          <p>{video.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="videos-cta">
            <h2>Have Questions About a Procedure?</h2>
            <p>
              Our team is here to answer any questions you may have about the procedures shown in these videos. 
              Contact us today to schedule a consultation.
            </p>
            <div className="cta-buttons">
              <Link to="/appointment-request" className="btn btn-primary">
                Request Appointment
              </Link>
              <Link to="/contact-us" className="btn btn-outline-dark">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {activeVideo && (
        <div className="video-modal" onClick={closeVideoPlayer}>
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={closeVideoPlayer}>×</button>
            <div className="video-wrapper">
              <video
                controls
                autoPlay
                src={activeVideo.videoUrl}
                className="video-player"
              >
                Your browser does not support the video tag.
              </video>
            </div>
            <div className="video-modal-info">
              <h3>{activeVideo.title}</h3>
              <p>{activeVideo.description}</p>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default DentalVideosPage;
